import React, { useState } from 'react';
import Chance from 'chance';
import PropTypes from 'prop-types';
import { Zap, RefreshCw, Search, Upload } from 'react-feather';
import { intlShape, FormattedMessage } from 'react-intl';

import StarBackground from 'components/star-background';
import Header, { HeaderType } from 'primitives/text/header';
import FlexContainer from 'primitives/container/flex-container';
import ContentContainer from 'primitives/container/content-container';
import SubContainer from 'primitives/container/sub-container';
import Checkbox from 'primitives/form/checkbox';
import LinkIcon from 'primitives/other/link-icon';
import Button from 'primitives/other/button';
import ItemRow from 'primitives/other/item-row';
import LabeledInput from 'primitives/form/labeled-input';

import { MIN_DIMENSION, MAX_DIMENSION } from 'constants/defaults';
import { generateSectorName } from 'utils/name-generator';
import {
  importSectorDefinition,
  summarizeSectorImport,
} from 'utils/import-sector';

import './style.scss';

const ImportHierarchy = ({ node }) => {
  const label = `${node.name} (${node.entityType})${
    node.isHidden ? ' - hidden' : ''
  }`;
  if (!node.children.length) {
    return <li>{label}</li>;
  }
  return (
    <li>
      <details>
        <summary>{label}</summary>
        <ul>
          {node.children.map(child => (
            <ImportHierarchy key={child.entityId} node={child} />
          ))}
        </ul>
      </details>
    </li>
  );
};

ImportHierarchy.propTypes = {
  node: PropTypes.shape({
    entityId: PropTypes.string.isRequired,
    entityType: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isHidden: PropTypes.bool.isRequired,
    children: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  }).isRequired,
};

export default function Configure({
  additionalPointsOfInterest,
  updateConfiguration,
  openCustomTagModal,
  generateSector,
  importSector,
  hideOccAndSit,
  useCustomTags,
  isLoggedIn,
  sectorName,
  isBuilder,
  hideTags,
  columns,
  rows,
  intl,
}) {
  const [importText, setImportText] = useState('');
  const [importErrors, setImportErrors] = useState([]);
  const [importPreview, setImportPreview] = useState(null);

  const updateInput = ({ target }) => {
    const key = target.getAttribute('data-key');
    let { value } = target;
    if (target.type === 'number') {
      value = value ? Number.parseInt(value, 10) : undefined;
    } else if (target.type === 'checkbox') {
      value = target.checked;
    }
    updateConfiguration(key, value);
  };

  const regenerateName = genFunc => () => {
    const chance = new Chance();
    updateConfiguration('sectorName', genFunc(chance));
  };

  const isValid = () =>
    !!sectorName &&
    !!rows &&
    !!columns &&
    rows <= MAX_DIMENSION &&
    rows >= MIN_DIMENSION &&
    columns <= MAX_DIMENSION &&
    columns >= MIN_DIMENSION;

  const readImportFile = event => {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImportText(reader.result);
      setImportErrors([]);
      setImportPreview(null);
    };
    reader.readAsText(file);
  };

  const onImport = () => {
    try {
      importSector(importText);
      setImportErrors([]);
    } catch (error) {
      setImportErrors(error.errors || [{ path: '$', message: error.message }]);
    }
  };

  const onPreview = () => {
    try {
      const imported = importSectorDefinition(importText);
      setImportPreview(summarizeSectorImport(imported));
      setImportErrors([]);
    } catch (error) {
      setImportPreview(null);
      setImportErrors(error.errors || [{ path: '$', message: error.message }]);
    }
  };

  return (
    <StarBackground>
      <ContentContainer direction="column" align="center" justify="center">
        <Header type={HeaderType.header2}>
          <FormattedMessage id="misc.configure" />
        </Header>
        <SubContainer noMargin direction="column" align="flexStart">
          <LabeledInput
            isVertical
            label="misc.sectorName"
            name="name"
            data-key="sectorName"
            icon={RefreshCw}
            value={sectorName}
            onChange={updateInput}
            onIconClick={regenerateName(generateSectorName)}
          />
          <ItemRow>
            <LabeledInput
              isVertical
              label="misc.rows"
              data-key="rows"
              onChange={updateInput}
              name="rows"
              type="number"
              value={rows || ''}
            />
            <LabeledInput
              isVertical
              label="misc.columns"
              data-key="columns"
              onChange={updateInput}
              name="columns"
              type="number"
              value={columns || ''}
            />
          </ItemRow>
          <p className="Configure-Info">
            <FormattedMessage
              id="misc.dimensionBounds"
              values={{ minNumber: MIN_DIMENSION, maxNumber: MAX_DIMENSION }}
            />
          </p>
          {isLoggedIn && (
            <FlexContainer
              align="flexEnd"
              justify="spaceBetween"
              className="Configure-ManageContainer"
            >
              <Checkbox
                data-key="useCustomTags"
                value={useCustomTags}
                onChange={updateInput}
                label={intl.formatMessage({ id: 'misc.useCustomTags' })}
              />
              <Button
                minimal
                className="Configure-ManageLink"
                onClick={openCustomTagModal}
              >
                (<FormattedMessage id="misc.manage" />)
              </Button>
            </FlexContainer>
          )}
          <Checkbox
            data-key="isBuilder"
            value={isBuilder}
            onChange={updateInput}
            label={intl.formatMessage({ id: 'misc.initializeEmpty' })}
          />
          <Checkbox
            data-key="additionalPointsOfInterest"
            value={additionalPointsOfInterest}
            onChange={updateInput}
            label={intl.formatMessage({ id: 'misc.useAPOI' })}
          />
          <Checkbox
            data-key="hideTags"
            value={hideTags}
            onChange={updateInput}
            label={intl.formatMessage({ id: 'misc.hideTagsFromPlayers' })}
          />
          <Checkbox
            data-key="hideOccAndSit"
            value={hideOccAndSit}
            onChange={updateInput}
            label={intl.formatMessage({ id: 'misc.hideOccAndSit' })}
          />
        </SubContainer>
        <SubContainer
          className="Configure-PaddedButtons"
          wrap
          justify="center"
          align="center"
        >
          <Button disabled={!isValid()} onClick={generateSector}>
            <LinkIcon icon={Zap} size="20" />
            <FormattedMessage id="misc.generate" />
          </Button>
        </SubContainer>
        <SubContainer
          className="Configure-Import"
          direction="column"
          align="flexStart"
        >
          <Header type={HeaderType.header3}>
            <FormattedMessage
              id="misc.importSectorJson"
              defaultMessage="Import Sector JSON"
            />
          </Header>
          <p className="Configure-Info">
            <FormattedMessage
              id="misc.importSectorJsonHelp"
              defaultMessage="Choose a JSON file or paste a version 1 sector definition. The file is validated before the sector is created."
            />
          </p>
          <input
            className="Configure-ImportFile"
            type="file"
            accept="application/json,.json"
            onChange={readImportFile}
          />
          <textarea
            className="Configure-ImportText"
            aria-label="Sector JSON"
            value={importText}
            onChange={event => {
              setImportText(event.target.value);
              setImportErrors([]);
              setImportPreview(null);
            }}
            placeholder={'{\n  "version": 1,\n  "sector": { ... }\n}'}
          />
          {importErrors.length > 0 && (
            <ul className="Configure-ImportErrors">
              {importErrors.map(({ path, message }) => (
                <li key={`${path}-${message}`}>
                  {path}: {message}
                </li>
              ))}
            </ul>
          )}
          {importPreview && (
            <div className="Configure-ImportPreview">
              <Header type={HeaderType.header4} noMargin>
                {importPreview.name} ({importPreview.columns} x{' '}
                {importPreview.rows})
              </Header>
              <p>
                {importPreview.entities} entities,{' '}
                {importPreview.hiddenEntities} hidden; {importPreview.notes}{' '}
                notes, {importPreview.hiddenNotes} hidden
              </p>
              <p>
                {importPreview.factions} factions, {importPreview.assets}{' '}
                assets, {importPreview.layers} layers, {importPreview.routes}{' '}
                routes
              </p>
              <details>
                <summary>Counts by entity type</summary>
                <ul>
                  {Object.keys(importPreview.entitiesByType)
                    .filter(type => importPreview.entitiesByType[type] > 0)
                    .map(type => (
                      <li key={type}>
                        {type}: {importPreview.entitiesByType[type]}
                      </li>
                    ))}
                </ul>
              </details>
              <details>
                <summary>Imported hierarchy</summary>
                <ul className="Configure-ImportHierarchy">
                  <ImportHierarchy node={importPreview.hierarchy} />
                </ul>
              </details>
              {importPreview.warnings.length > 0 && (
                <div className="Configure-ImportWarnings">
                  <b>Warnings</b>
                  <ul>
                    {importPreview.warnings.map(warning => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <FlexContainer wrap align="center">
            <Button disabled={!importText.trim()} onClick={onPreview}>
              <LinkIcon icon={Search} size="20" />
              <FormattedMessage
                id="misc.previewImport"
                defaultMessage="Validate and Preview"
              />
            </Button>
            <Button disabled={!importPreview} onClick={onImport}>
              <LinkIcon icon={Upload} size="20" />
              <FormattedMessage
                id="misc.importSector"
                defaultMessage="Import Sector"
              />
            </Button>
          </FlexContainer>
        </SubContainer>
      </ContentContainer>
    </StarBackground>
  );
}

Configure.propTypes = {
  additionalPointsOfInterest: PropTypes.bool.isRequired,
  updateConfiguration: PropTypes.func.isRequired,
  openCustomTagModal: PropTypes.func.isRequired,
  generateSector: PropTypes.func.isRequired,
  importSector: PropTypes.func.isRequired,
  isBuilder: PropTypes.bool.isRequired,
  hideTags: PropTypes.bool.isRequired,
  hideOccAndSit: PropTypes.bool.isRequired,
  useCustomTags: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  sectorName: PropTypes.string,
  columns: PropTypes.number,
  rows: PropTypes.number,
  intl: intlShape.isRequired,
};

Configure.defaultProps = {
  columns: undefined,
  rows: undefined,
  sectorName: '',
};
